import {
  Arr,
  Bool,
  MergeObj2,
  MergeObj4,
  Num,
  Obj,
  Opt,
  OptBool,
  OptNum,
  OptObj,
  OptStr,
  SchemaToType,
  Str,
  Unsafe,
} from '@shared/api/core/api_schema';
import {Brand} from '@shared/lib/utils/type_utils';
import {PreciseTimestamp} from '@shared/models/core_models';
import {SupportedLanguage} from '@shared/models/language';
import {GroupMemberId, SupportMessageThreadId} from '@shared/models/mavismart_ids';
import {TermsVersion} from '@shared/models/term';

export const CodeRequestSchema = Obj({
  email: Str(),
});
export type CodeRequest = SchemaToType<typeof CodeRequestSchema>;

export const CodeResponseSchema = Obj({
  success: Bool(),
  nextCodeAt: OptStr(),
});
export type CodeResponse = SchemaToType<typeof CodeResponseSchema>;

export const LoginRequestSchema = Obj({
  email: Str(),
  code: Str(),
});
export type LoginRequest = SchemaToType<typeof LoginRequestSchema>;

export const ALL_NOTIFICATION_EMAIL_FREQUENCIES = [
  'never',
  'monthly',
  'weekly',
  'daily',
  'hourly',
] as const;
export type NotificationEmailFrequency = (typeof ALL_NOTIFICATION_EMAIL_FREQUENCIES)[number];

export const AccountSchema = Obj({
  email: Str(),
  phone: OptStr(),
  language: OptStr<SupportedLanguage>(),
  termsVersion: OptStr<TermsVersion>(),
  hasSupportApp: OptBool(), // Whether the user has access to the support app
  hasNotificationEmail: OptBool(), // Access to email notifications settings
  canDeleteAccount: OptBool(), // Whether the user can delete their account
  canCreateCustomGroup: OptBool(), // Whether the user can create custom groups
  fetchedAt: Str(), // Used here to discriminate with BackendAccount interface
  deletedAt: OptStr(),
  alertNotificationsEnabled: OptBool(),
  anomalyNotificationsEnabled: OptBool(),
  notificationEmailFrequency: OptStr<NotificationEmailFrequency>(),
  supportAppEnabled: OptBool(), // Whether the support app is enabled (default is false)
});
export type Account = SchemaToType<typeof AccountSchema>;

export const AcceptTermsSchema = Obj({
  termsVersion: Str<TermsVersion>(),
});
export type AcceptTerms = SchemaToType<typeof AcceptTermsSchema>;

export const UpdateAccountSchema = Obj({
  phone: OptStr(),
  language: OptStr(),
  alertNotificationsEnabled: OptBool(),
  anomalyNotificationsEnabled: OptBool(),
  notificationEmailFrequency: OptStr<NotificationEmailFrequency>(),
  supportAppEnabled: OptBool(),
});
export type UpdateAccount = SchemaToType<typeof UpdateAccountSchema>;

// Door Account Metadata

const FrontendDoorAccountMetadataSchemaData = {
  notificationMode: Str<DoorAccountNotificationMode>(),
};
export const ALL_DOOR_ACCOUNT_NOTIFICATION_MODES = ['default', 'force'] as const;
export type DoorAccountNotificationMode = (typeof ALL_DOOR_ACCOUNT_NOTIFICATION_MODES)[number];
export const FrontendDoorAccountMetadataSchema = Obj({
  notificationMode: Str<DoorAccountNotificationMode>(),
});
export type FrontendDoorAccountMetadata = SchemaToType<typeof FrontendDoorAccountMetadataSchema>;

// Tecmotion Metadata
const FrontendDoorTecmotionSchemaData = Obj({doorCycles: OptNum()});
export type FrontendDoorTecmotionData = SchemaToType<typeof FrontendDoorTecmotionSchemaData>;

export enum GroupType {
  Client = 10,
  Groupe = 20,
  Manual = 40,
}

const GroupLocationSchema = Obj({
  address1: OptStr(),
  address2: OptStr(),
  address3: OptStr(),
  postalCode: OptStr(),
  city: OptStr(),
  country: OptStr(),
});
export type GroupLocation = SchemaToType<typeof GroupLocationSchema>;

export const GroupContactSchema = Obj({
  contactFirstName: OptStr(),
  contactLastName: OptStr(),
  contactPhone: OptStr(),
  contactEmail: OptStr(),
});
export type GroupContact = SchemaToType<typeof GroupContactSchema>;
export const GroupContactSchema2 = Obj({
  contactFirstName2: OptStr(),
  contactLastName2: OptStr(),
  contactPhone2: OptStr(),
  contactEmail2: OptStr(),
});
export type GroupContact2 = SchemaToType<typeof GroupContactSchema2>;

export const GroupInfoSchema = Obj({
  id: Str(),
  type: Num<GroupType>(),
  parentGroupId: OptStr(),
  name: OptStr(),
});
export type GroupInfo = SchemaToType<typeof GroupInfoSchema>;

export const GroupSchema = MergeObj4(
  GroupLocationSchema,
  GroupContactSchema,
  GroupContactSchema2,
  GroupInfoSchema
);
export type Group = SchemaToType<typeof GroupSchema>;

export const DirectGroupSchema = Obj({
  location: Opt(GroupLocationSchema),
  contact: Opt(GroupContactSchema),
  contact2: Opt(GroupContactSchema),
  info: Opt(GroupInfoSchema),
  from: Arr(Str<'buyer' | 'maintainer' | 'customer'>()),
});
export type DirectGroup = SchemaToType<typeof DirectGroupSchema>;

export type GroupMemberPermission = 'admin' | undefined;

export const SupportGroupMemberSchema = Obj({
  id: Str<GroupMemberId>(),
  email: Str(),
  permission: Str<GroupMemberPermission>(),
  groupId: Str(),
});
export type SupportGroupMember = SchemaToType<typeof SupportGroupMemberSchema>;

export const FullGroupSchema = Obj({
  group: GroupSchema,
  permission: OptStr<GroupMemberPermission>(),
  doorIds: Arr(Str()),
});
export type FullGroup = SchemaToType<typeof FullGroupSchema>;
export function groupName(fullGroup: FullGroup): string {
  if (fullGroup.group.name) {
    return fullGroup.group.name;
  }
  return formatGroupId(fullGroup.group.id);
}
export function formatGroupId(groupId: string): string {
  const lastGroupId = groupId.split('__').at(-1);
  return lastGroupId ?? groupId;
}

export const DoorStateSchema = Obj({
  ts: Num(),
  state: Unsafe<Record<string, unknown>>(),
  simId: OptStr(),
});
export type DoorState = SchemaToType<typeof DoorStateSchema>;

export const ALL_COUNTER_BREAKDOWN = ['hour', 'day'] as const;
export type CounterBreakdown = (typeof ALL_COUNTER_BREAKDOWN)[number];

export const DoorCountersDataPointSchema = Unsafe<
  Partial<Record<DoorCounterName, number>> & {ts: number}
>();
export type DoorCountersDataPoint = SchemaToType<typeof DoorCountersDataPointSchema>;

export const ALL_DOOR_MESSAGE_TYPES = ['alert', 'anomaly'] as const;
export type DoorMessageType = (typeof ALL_DOOR_MESSAGE_TYPES)[number];

export const ALL_DOOR_MESSAGE_LEVELS = ['green', 'yellow', 'red'] as const;
export type DoorMessageLevel = (typeof ALL_DOOR_MESSAGE_LEVELS)[number];

export const ALL_ALERT_MESSAGE_CATEGORIES = [
  'problem_brake_replay_at_shutdown',
  'detection_of_the_belt_breakage',
  'detection_of_the_duration_of_movement_too_long',
  'incorrect_max_current_setting',
  'cell_test_erreur',
  'encoder_error_or_encoder_disconnected',
  'engine_configuration_problem_or_connection_problem',
  'igbt_electronic_temperature_problem',
  'problem_card_configation',
  'lack_of_braking_resistance',
  'emergency_stop',
  'stop_button',
  'auto_manu',
  'contacteur_fbk_fault',
] as const;
export type AlertMessageCategory = (typeof ALL_ALERT_MESSAGE_CATEGORIES)[number];

export const ALL_ANOMALY_MESSAGE_CATEGORIES = ['door_cycles_warning'] as const;
export type AnomalyMessageCategory = (typeof ALL_ANOMALY_MESSAGE_CATEGORIES)[number];

export type UnknownAlertMessageCategory = Brand<string, 'UnknownAlertMessageCategory'>;

export type DoorMessageCategory =
  | AlertMessageCategory
  | UnknownAlertMessageCategory
  | AnomalyMessageCategory;

export const ALL_DOOR_COUNTER_NAMES = [
  'door_cycles',
  'door_time_open',
  'door_inversion_button',
  'door_inversion_cell',
  'door_inversion_impact',
  'cell_dirty',
  'door_close',
  'door_open',
  'counter_IC0',
  'counter_IC1',
  'counter_I5',
  ...ALL_ALERT_MESSAGE_CATEGORIES,
] as const;
export type DoorCounterName = (typeof ALL_DOOR_COUNTER_NAMES)[number];

export const FrontendDoorMessageSchema = Obj({
  ts: Num<PreciseTimestamp>(),
  type: Num<DoorMessageType>(),
  category: Str<DoorMessageCategory>(),
  level: Str<DoorMessageLevel>(),
  doorCycles: OptNum(),
});
export type FrontendDoorMessage = SchemaToType<typeof FrontendDoorMessageSchema>;

export const ALL_ACCOUNT_NOTIFICATION_TYPES = ['door_message'] as const;
export type AccountNotificationType = (typeof ALL_ACCOUNT_NOTIFICATION_TYPES)[number];

export const ALL_ACCOUNT_NOTIFICATION_STATUSES = ['unread', 'read'] as const;
export type AccountNotificationStatus = (typeof ALL_ACCOUNT_NOTIFICATION_STATUSES)[number];

export const FrontendAccountNotificationSchema = Obj({
  status: Str<AccountNotificationStatus>(),
  ts: Num<PreciseTimestamp>(),
  type: Str<AccountNotificationType>(),
  doorId: OptStr(),
  doorMessage: Opt(FrontendDoorMessageSchema),
});
export type FrontendAccountNotification = SchemaToType<typeof FrontendAccountNotificationSchema>;

export type MediaId = Brand<string, 'MediaId'>;

export const FrontendMediaSchema = Obj({
  id: Str<MediaId>(),
  contentType: Str(),
  contentLength: Num(),
  fileName: Str(),
  url: Str(),
  ts: Num(),
});
export type FrontendMedia = SchemaToType<typeof FrontendMediaSchema>;

//
// Support Message
//

export type SupportMessageId = Brand<string, 'SupportMessageId'>;

export const FrontendSupportMessageSchema = Obj({
  id: Str<SupportMessageId>(),
  threadId: Str<SupportMessageThreadId>(),
  email: Str(),
  doorId: OptStr(),
  doorMessageTs: OptNum<PreciseTimestamp>(),
  content: Str(),
  mediaIds: Arr(Str<MediaId>()),
  ts: Num<PreciseTimestamp>(),
});
export type FrontendSupportMessage = SchemaToType<typeof FrontendSupportMessageSchema>;

// Category Levels

const CategoryLevelsSchema = OptObj(
  Object.fromEntries(
    [...ALL_ALERT_MESSAGE_CATEGORIES, ...ALL_ANOMALY_MESSAGE_CATEGORIES].map(category => [
      category,
      OptStr<DoorMessageLevel>(),
    ])
  ) as Record<DoorMessageCategory, ReturnType<typeof OptStr<DoorMessageLevel>>>
);

// Door Metadata

const FrontendDoorMetadataSchemaData = {
  label: OptStr(),
  status: OptStr<DoorMessageLevel>(),
  customAddress: OptStr(),
  categoryLevels: CategoryLevelsSchema,
};
export const FrontendDoorMetadataSchema = Obj(FrontendDoorMetadataSchemaData);
export type FrontendDoorMetadata = SchemaToType<typeof FrontendDoorMetadataSchema>;

// Door

export const DoorSchemaWithoutDoorCycles = Obj({
  id: Str(),
  directGroups: Arr(DirectGroupSchema),
  name1: OptStr(),
  name2: OptStr(),
  name3: OptStr(),
  width: OptNum(),
  height: OptNum(),
  widthHt: OptNum(),
  heightHt: OptNum(),
  customerName: OptStr(),
  setupDate: OptStr(),
  address1: OptStr(),
  address2: OptStr(),
  address3: OptStr(),
  postalCode: OptStr(),
  city: OptStr(),
  country: OptStr(),
  warrantyEndDate: OptStr(),
  curtainColor: OptStr(),
  classification: OptStr(),
  structureColor: OptStr(),
  voltage: OptStr(),
  cabinet: OptStr(),
  ...FrontendDoorMetadataSchemaData,
  ...FrontendDoorAccountMetadataSchemaData,
  fetchedAt: Str(), // Used here to discriminate with X3Door interface
});
export type DoorWithoutDoorCycles = SchemaToType<typeof DoorSchemaWithoutDoorCycles>;

export const DoorSchema = MergeObj2(DoorSchemaWithoutDoorCycles, FrontendDoorTecmotionSchemaData);
export type Door = SchemaToType<typeof DoorSchema>;
